import { useReveal } from '@/hooks/useReveal';

const workflowSteps = [
  {
    step: '01',
    icon: 'ri-user-voice-line',
    title: 'Customer Messages You',
    description: 'Customer sends a query on WhatsApp — product enquiry, appointment request, or support need.',
    color: 'from-primary-500/20 to-primary-400/10',
    iconBg: 'bg-primary-100',
    iconColor: 'text-primary-600',
  },
  {
    step: '02',
    icon: 'ri-whatsapp-line',
    title: 'AI Instantly Responds',
    description: 'DGFlow\'s AI chatbot understands intent, answers FAQs, collects information, and qualifies the lead — 24/7.',
    color: 'from-accent-500/20 to-accent-400/10',
    iconBg: 'bg-accent-100',
    iconColor: 'text-accent-600',
  },
  {
    step: '03',
    icon: 'ri-brain-line',
    title: 'Smart Automation Kicks In',
    description: 'Automated workflows trigger: lead assignment, appointment booking, payment reminders, and follow-up sequences.',
    color: 'from-secondary-500/20 to-secondary-400/10',
    iconBg: 'bg-secondary-100',
    iconColor: 'text-secondary-600',
  },
  {
    step: '04',
    icon: 'ri-database-2-line',
    title: 'CRM Organizes Everything',
    description: 'All conversations, contacts, deals, and tasks are automatically organized in one beautiful dashboard.',
    color: 'from-violet-500/20 to-violet-400/10',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
  },
  {
    step: '05',
    icon: 'ri-hand-coin-line',
    title: 'Close More Sales',
    description: 'Your team focuses on closing — not chasing. With automated nurturing, conversion rates jump 3-5x.',
    color: 'from-amber-500/20 to-amber-400/10',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  {
    step: '06',
    icon: 'ri-bill-line',
    title: 'Instant Invoicing & Payment',
    description: 'Send invoices, payment links, and collect payments directly on WhatsApp. No app switching needed.',
    color: 'from-emerald-500/20 to-emerald-400/10',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  {
    step: '07',
    icon: 'ri-star-line',
    title: 'Automated Reviews & Feedback',
    description: 'After every sale, automatically request reviews. Build your reputation while you sleep.',
    color: 'from-rose-500/20 to-rose-400/10',
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
  },
  {
    step: '08',
    icon: 'ri-loop-left-line',
    title: 'Repeat Business, Automated',
    description: 'AI identifies repeat purchase opportunities and re-engages customers at the perfect moment.',
    color: 'from-cyan-500/20 to-cyan-400/10',
    iconBg: 'bg-cyan-100',
    iconColor: 'text-cyan-600',
  },
];

export default function SolutionSection() {
  const { ref, visible } = useReveal();

  return (
    <section className="relative py-20 md:py-28 bg-background-100 overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 md:px-6 reveal ${visible ? 'visible' : ''}`}>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold uppercase tracking-wider mb-5">
            The DGFlow Solution
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-5 leading-tight">
            From{' '}
            <span className="text-foreground-400 line-through decoration-rose-400 decoration-2">Chaos</span>
            {' '}to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-accent-500">
              Complete Control
            </span>
          </h2>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
            DGFlow transforms your WhatsApp from a chaotic messaging app into a powerful business engine.
            Here&apos;s how every customer journey becomes automated revenue.
          </p>
        </div>

        {/* Workflow Diagram */}
        <div className="relative max-w-4xl mx-auto">
          {/* Connecting line */}
          <div className="absolute left-[23px] md:left-1/2 md:-translate-x-px top-12 bottom-12 w-0.5 bg-gradient-to-b from-primary-300 via-accent-300 to-primary-300 hidden md:block"></div>

          <div className="space-y-0">
            {workflowSteps.map((item, i) => (
              <div
                key={item.step}
                className={`relative flex flex-col md:flex-row gap-4 md:gap-0 items-start py-5 md:py-8 group ${
                  i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Connector dot */}
                <div className="absolute left-[18px] md:left-1/2 md:-translate-x-1/2 top-10 w-3 h-3 rounded-full bg-primary-500 border-4 border-background-100 z-10 shadow-sm"></div>

                {/* Content Card */}
                <div className={`md:w-[calc(50%-40px)] ${i % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'} pl-12 md:pl-0`}>
                  <div className={`bg-background-50 border border-background-200/70 rounded-2xl p-5 md:p-6 hover:border-primary-200/50 transition-all duration-400 group-hover:-translate-y-0.5 ${
                    i % 2 === 0 ? 'md:items-end' : 'md:items-start'
                  } flex flex-col`}>
                    <span className="text-xs font-bold text-primary-500 mb-2 tracking-wider">{item.step}</span>
                    <div className={`flex items-center gap-3 mb-3 ${i % 2 === 0 ? 'md:flex-row-reverse' : 'md:flex-row'}`}>
                      <div className={`w-10 h-10 rounded-xl ${item.iconBg} flex items-center justify-center flex-shrink-0`}>
                        <i className={`${item.icon} text-lg ${item.iconColor}`}></i>
                      </div>
                      <h3 className="text-base font-bold font-heading text-foreground-950">{item.title}</h3>
                    </div>
                    <p className={`text-sm text-foreground-600 leading-relaxed ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      {item.description}
                    </p>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden md:block md:w-[calc(50%-40px)]"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}